/**
 * Shared Sentry client configuration for Next.js apps.
 *
 * The primary defense is structural: `sentryBeforeSend` uses a first-party
 * frame allowlist so that errors from browser extensions, crypto wallets, and
 * injected scripts are dropped without maintaining an ever-growing denylist.
 *
 * `sentryIgnoreErrors` is a secondary layer for non-actionable errors that
 * DO have first-party frames (e.g. network transients from our fetch calls).
 */

export const sentryIgnoreErrors: (string | RegExp)[] = [
  // Fires in all browsers, not actionable
  'ResizeObserver loop limit exceeded',

  // Network transient errors — our code is in the stack, but the error is the network
  /^Error: Connection closed\.$/,
  /Error in input stream/i,
  /Failed to fetch/i,
  /fetch failed/,
  /is an invalid header value/i, // Bots/scanners sending malformed headers
  'TypeError: Load failed',
  'TypeError: network error',
  'NetworkError: A network error occurred',
  /NetworkError when attempting to fetch resource/i,
  /Network connection lost/i,

  // Injected corporate/enterprise scripts (no distinctive filename to denylist)
  /sap is not defined/i, // SAP enterprise browser extension

  // React reconciler internal — no actionable stack trace
  'Unknown root exit status',
];

/**
 * Filename patterns that identify injected third-party code. If any stack
 * frame matches, the event is dropped even when first-party frames exist —
 * the error was triggered by injected code, not by our application.
 */
const injectedFramePatterns: RegExp[] = [
  // Browser internals
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,

  // Extensions and injected scripts
  /AdGuard%20Popup%20Blocker/i, // Popup blocker
  /brand-color-extractor\.js$/i, // Browser extension
  /btc\.js$/i, // Bitcoin wallet
  /CallingFuncation\.js$/i, // Malware (note misspelling)
  /chunk-inject\.js$/i, // Extension injection
  /content-main-world\.js$/i, // Extension content script
  /content\/captcha\//i, // Captcha-solving extension
  /extensions\//i,
  /extensionPageScript\.js$/i,
  /extensionServiceWorker\.js$/i,
  /frame_ant\//i, // Keplr wallet extension
  /gt-window-provider/i,
  /inject\.js$/i,
  /inject_flip\.js$/i, // Fingerprinting extension
  /injected\.js$/i, // Crypto wallet (TronLink etc.)
  /inpage\.js$/i, // Crypto wallet (MetaMask etc.)
  /inpage_as_fallback\.js$/i,
  /scripts\/sdk\/plugin\.js$/i, // Third-party SDK plugin
  /translate_http\//i, // Google Translate extension
  /userscript\.html$/i,
  /userscripts\/.*\.user\.js$/i, // Userscript managers (Greasemonkey etc.)
];

/**
 * Returns true when a stack frame originates from our webpack-bundled code.
 * Webpack source maps produce ./ or ../ paths; Next.js chunks contain /_next/.
 * Sentry SDK wrapper frames are excluded — they appear in every instrumented
 * error regardless of origin.
 *
 * For ../ paths, we additionally verify that abs_path is a real URL or that the
 * filename contains node_modules. Extensions can have their own source maps
 * that produce ../ paths (e.g. ../../src/lies/index.ts from a fingerprinting
 * library), but their abs_path will be a bare relative path rather than a URL.
 *
 * Infrastructure chunks are excluded from the /_next/ check — the webpack
 * runtime bootstrap (`webpack-*.js`) and route container chunks (`app/`,
 * `pages/`) are present in every error that fires within a Next.js page and
 * are not a meaningful signal of first-party origin.
 */
const SENTRY_FRAME_RE = /@sentry[/+]/;
const HTTP_RE = /^https?:\/\//;
const INFRA_CHUNK_RE = /\/_next\/static\/chunks\/(webpack-|app\/|pages\/)/;
const hasFirstPartyFrame = (filename: string, absPath: string): boolean =>
  !SENTRY_FRAME_RE.test(filename) &&
  ((filename.startsWith('./') && absPath.includes('/_next/')) ||
    (filename.startsWith('../') &&
      (HTTP_RE.test(absPath) || filename.includes('node_modules'))) ||
    (filename.includes('/_next/') && !INFRA_CHUNK_RE.test(filename)));

/**
 * `beforeSend` callback that drops events not originating from our code.
 *
 * Four layers, evaluated in order:
 *
 * 1. **Injection denylist** — if any frame matches a known injected-code
 *    filename (extensions, userscripts, wallets, translate widgets), the
 *    event is dropped even when first-party frames exist.
 *
 * 2. **First-party allowlist** — if an event has stack frames but *none* come
 *    from our webpack-bundled code, it is extension / injected-script noise
 *    and is dropped.
 *
 * 3. **Frameless-event drop** — client-side errors from our code always
 *    produce stack traces (source-mapped bundles). Any exception without
 *    frames is not debuggable and is dropped.
 *
 * 4. **Message-based ignore** — errors whose message matches
 *    `sentryIgnoreErrors` are dropped even if they have first-party frames
 *    (e.g. React internals that surface through our bundled dependencies).
 */
export const sentryBeforeSend = <
  T extends {
    exception?: {
      values?: {
        value?: string;
        stacktrace?: {
          frames?: { filename?: string; abs_path?: string }[];
        };
        mechanism?: {
          type?: string;
          handled?: boolean;
          synthetic?: boolean;
        };
      }[];
    };
  },
>(
  event: T,
  _hint: unknown,
): T | null => {
  const exceptions = event.exception?.values ?? [];

  // Events with no exceptions carry no debugging value.
  if (exceptions.length === 0) {
    return null;
  }

  // Injection denylist — drop if any frame is from known injected code
  for (const exception of exceptions) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const paths = [frame.filename, frame.abs_path].filter(Boolean);
      for (const path of paths) {
        if (injectedFramePatterns.some((p) => p.test(path!))) {
          return null;
        }
      }
    }
  }

  // First-party allowlist
  let totalFrames = 0;
  let hasFirstParty = false;
  for (const exception of exceptions) {
    for (const frame of exception.stacktrace?.frames ?? []) {
      const filename = frame.filename ?? frame.abs_path ?? '';
      if (!filename) continue;
      totalFrames++;
      const absPath = frame.abs_path ?? '';
      if (!hasFirstParty && hasFirstPartyFrame(filename, absPath)) {
        hasFirstParty = true;
      }
    }
  }

  if (totalFrames > 0 && !hasFirstParty) {
    return null;
  }

  // Drop frameless errors (not debuggable regardless of origin)
  if (totalFrames === 0) {
    return null;
  }

  // Drop errors matching sentryIgnoreErrors
  for (const ex of exceptions) {
    const msg = ex.value ?? '';
    if (
      sentryIgnoreErrors.some((p) =>
        typeof p === 'string' ? msg.includes(p) : p.test(msg),
      )
    ) {
      return null;
    }
  }

  return event;
};

/**
 * Transaction name patterns to always drop (sample rate = 0).
 * These provide no debugging value and generate high volume.
 */
const noiseTransactionPatterns: RegExp[] = [
  /^middleware /i, // Next.js middleware (static assets, rewrites)
  /^NextNodeServer\./, // Next.js internal server operations
  /^start response$/, // Next.js internal response handling
  /POST .*events\.launchdarkly\.com/i, // LaunchDarkly event flush
  /POST .*\/api\/\d+\/envelope/i, // Sentry SDK envelope posts
  /^GET \/api\/health$/, // Health check endpoints
  /^GET \/health$/,
  /^GET \/_next\//, // Next.js static asset requests
];

/**
 * Creates a `tracesSampler` that drops noisy transactions across all layers
 * (client, server, edge) and falls back to the given sample rate.
 *
 * Dropped transactions include Next.js middleware and internals,
 * LaunchDarkly event flushes, Sentry envelope posts, health checks,
 * and static asset requests. The sampler also preserves distributed trace
 * continuity via `parentSampled`.
 */
export function createTracesSampler(fallbackRate: number) {
  return ({
    name,
    parentSampled,
  }: {
    name: string;
    parentSampled?: boolean;
  }): number => {
    if (name && noiseTransactionPatterns.some((p) => p.test(name))) {
      return 0;
    }
    if (parentSampled !== undefined) {
      return parentSampled ? 1 : 0;
    }
    return fallbackRate;
  };
}
